import type { MutationUpdaterFn } from 'apollo-client';

import type { Comment } from '../src/useAllCommentsData';
import {
	resolveCommentInCache,
	type UpdateCommentCallback,
	type ResolveInlineCommentMutationResultData,
	type ResolveCommentsMutationResultData,
	type ReopenCommentMutationResultData,
} from '../src/useUpdateCommentInCache';

export const findCommentInData = (
	commentData: Comment[],
	commentId: string,
): Comment | undefined => {
	// Search in retreived comments and their replies
	for (const comment of commentData) {
		// Check if this is the comment we're looking for
		if (comment?.id === commentId) {
			return comment;
		}

		// Check if it's in the replies
		if (comment?.replies) {
			const foundReply = comment.replies.find((reply) => reply?.id === commentId);
			if (foundReply) {
				return foundReply as Comment;
			}
		}
	}

	// else comment not found
	return undefined;
};

// To avoid Sonar errors about nested ternaries, as the false functions (the legacy code paths for
// cache updates) often contain ternaries
export const selectUpdateFunction = <TResultType>(
	condition: boolean,
	trueFn?: MutationUpdaterFn<TResultType>,
	falseFn?: MutationUpdaterFn<TResultType>,
) => (condition ? trueFn : falseFn);

/**
 * Finds the root (top-level) comment that contains a reply with the given ID
 * Note that the reply arrays are actually flat arrays of Comment objects - nested comments are not
 * actually nested in the GQL data schema but stored flat in the root parent's replies array
 * with the parentId field that indicates which comment they really reply to (we build the actual
 * nested structure elsewhere in the FE). So this function finds where you should add a reply -
 * even a nested reply - in the schema (or undefined if the parent is not found in the data).
 * @param commentData - Array of root level comments to search through
 * @param replyParentId - The parent ID of the reply to find
 * @returns The root comment that contains the reply, or undefined if not found
 */
export const findRootCommentForReply = (
	commentData: Comment[],
	replyParentId: string,
): Comment | undefined => {
	// Search through each root level comment's replies

	for (const comment of commentData) {
		// Return this comment if it is the parent of the reply, else check if this comment has replies
		// and if any of them have the matching parentId
		if (
			comment?.id === replyParentId ||
			comment?.replies.find((reply) => reply?.id === replyParentId)
		) {
			return comment;
		}
	}

	// Reply parent not found
	return undefined;
};

/**
 * Updates an inline comment fragment as resolved or reopened after a resolveInlineComment()
 * mutation, based on the result of a "resolved" property in the mutation result.  This is
 * effectively a wrapper around resolveCommentInCache() that extracts the relevant properties from
 * the mutation result - but is in a separate file so it can be unit tested with a spied
 * resolveCommentInCache(), since you can't mock or spy on a function that is called directly
 * within the same file.
 * @param comment - the current cached inline comment fragment
 * @param mutationResult - the result of the resolveInlineComment() mutation
 * @returns - the updated comment fragment, to be written back to the cache
 */
export const resolveInlineCommentInCacheCallback: UpdateCommentCallback<
	ResolveInlineCommentMutationResultData
> = (comment, mutationResult) => {
	const resolveInlineComment = mutationResult?.data?.resolveInlineComment;
	const mutationStatus = resolveInlineComment?.status ?? false;
	const resolveProperties = resolveInlineComment?.resolveProperties ?? undefined;

	return resolveCommentInCache({ comment, mutationStatus, resolveProperties });
};

/**
 * Updates a comment fragment as resolved. after a confluence_resolveComments() mutation, based on
 * the result of a "resolved" property in the mutation result.  This is effectively a wrapper
 * around resolveCommentInCache() that extracts the relevant properties from the mutation result -
 * but is in a separate file so it can be unit tested with a spied resolveCommentInCache(), since
 * you can't mock or spy on a function that is called directly within the same file.
 *
 * Although the mutation can resolve multiple comments at once, this function assumes that ONLY
 * ONE COMMENT PER MUTATION CALL is being updated. That is necessary because the updateCommentInCache()
 * updater function only works on a single comment - and it is okay because the current code only
 * ever resolves one comment at a time with this mutation.  Therefore this function finds the first
 * comment resolution state from the array in the mutation result.
 * @param comment - the current cached inline comment fragment
 * @param mutationResult - the result of the confluence_resolveComments() mutation
 * @returns - the updated comment fragment, to be written back to the cache
 */
export const resolveCommentInCacheCallback: UpdateCommentCallback<
	ResolveCommentsMutationResultData
> = (comment, mutationResult) => {
	const commentResolutionStates =
		mutationResult?.data?.confluence_resolveComments?.commentResolutionStates;
	if ((commentResolutionStates?.length ?? 0) > 1) {
		throw new Error(
			'resolveCommentInCacheCallback() only supports one comment resolution state per mutation call',
		);
	}
	const firstResolutionState = commentResolutionStates?.[0] ?? undefined;
	const mutationStatus = firstResolutionState?.status ?? false;
	const partialResolveProperties = firstResolutionState?.resolveProperties ?? undefined;

	// We need to ensure that resolveProperties contains all non-optional properties so we can safely
	// spread it into the comment location resolve properties, so choose defaults for any missing
	// ones.  This is needed because some of the mutations don't request all the properties in their
	// responses.
	const resolveProperties = partialResolveProperties
		? {
				resolved: true, // This mutation only resolves comments
				isDangling: false, // Assumes resolved comments are never dangling
				// These are nullable in the schema, but included to make Typescript happy
				resolvedUser: undefined,
				resolvedFriendlyDate: undefined,
				resolvedTime: undefined,
				// Add any non-null/undefined properties from the mutation result
				...partialResolveProperties,
			}
		: undefined;

	return resolveCommentInCache({ comment, mutationStatus, resolveProperties });
};

/**
 * Updates a comment fragment as reopened after a confluence_reopenComment() mutation, based on the
 * result of a "resolved" property in the mutation result.  This is effectively a wrapper around
 * resolveCommentInCache() that extracts the relevant properties from the mutation result - but is
 * in a separate file so it can be unit tested with a spied resolveCommentInCache(), since you
 * can't mock or spy on a function that is called directly within the same file.
 * @param comment - the current cached inline comment fragment
 * @param mutationResult - the result of the confluence_reopenComment() mutation
 * @returns - the updated comment fragment, to be written back to the cache
 */
export const reopenCommentInCacheCallback: UpdateCommentCallback<
	ReopenCommentMutationResultData
> = (comment, mutationResult) => {
	// (The property is called "commentResolutionStates" but there is only one item per mutation call)
	const commentResolutionStates =
		mutationResult?.data?.confluence_reopenComment?.commentResolutionStates;

	if (commentResolutionStates) {
		const mutationStatus = commentResolutionStates.status ?? false;
		const partialResolveProperties = commentResolutionStates.resolveProperties ?? undefined;

		// We need to ensure that resolveProperties contains all non-optional properties so we can safely
		// spread it into the comment location resolve properties, so choose defaults for any missing
		// ones. This is needed because some of the mutations don't request all the properties - or even
		// the commentResolutionStates property at all in their responses.
		const resolveProperties = {
			resolved: false, // This mutation only reopens comments
			isDangling: false, // Assumes reopened comments are never dangling
			// These are nullable in the schema, but included to make Typescript happy
			resolvedUser: undefined,
			resolvedFriendlyDate: undefined,
			resolvedTime: undefined,
			// Add any non-null/undefined properties from the mutation result
			...partialResolveProperties,
		};

		return resolveCommentInCache({ comment, mutationStatus, resolveProperties });
	}

	// Else commentResolutionStates wasn't returned - assume failure to reopen
	return resolveCommentInCache({ comment, mutationStatus: false, resolveProperties: undefined });
};
